import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, UpdateTenantRequest, Tenant } from '../api/client';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ModeToggle } from '@/components/mode-toggle';
import { DynamicLogo } from '@/components/DynamicLogo';

export function TenantSetup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UpdateTenantRequest>({
    name: '',
  });
  const [, setCurrentTenant] = useState<Tenant | null>(null);
  const [errors, setErrors] = useState<Partial<UpdateTenantRequest>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTenant, setIsLoadingTenant] = useState(true);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadCurrentTenant();
  }, []);

  const loadCurrentTenant = async () => {
    try {
      const tenant = await apiClient.getTenant();
      setCurrentTenant(tenant);
      setFormData({ name: tenant.name });
    } catch (error: any) {
      console.error('Failed to load tenant:', error);
      setApiError('Failed to load tenant information');
    } finally {
      setIsLoadingTenant(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<UpdateTenantRequest> = {};

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Organization name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      await apiClient.updateTenant(formData);
      setSuccessMessage('Organization setup completed successfully! Redirecting...');

      // New orgs go through the setup wizard next.
      setTimeout(() => {
        navigate('/setup');
      }, 1500);
    } catch (error: any) {
      setApiError(error.response?.data || 'Failed to update organization. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof UpdateTenantRequest]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };



  if (isLoadingTenant) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:bg-background dark:from-transparent dark:via-transparent dark:to-transparent flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:bg-background dark:from-transparent dark:via-transparent dark:to-transparent flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Theme toggle */}
      <div className="absolute top-6 right-6">
        <ModeToggle />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <DynamicLogo size="xl" className="drop-shadow-lg" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          Welcome to Kestrel AI
        </h2>
        <p className="mt-2 text-center text-lg text-muted-foreground">
          AI Agents for Platform Engineering
        </p>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Tell us about your organization to get started
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Organization Details</CardTitle>
            <CardDescription>
              Customize your organization name and settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage && (
              <Alert className="mb-6">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{successMessage}</AlertDescription>
              </Alert>
            )}

            {apiError && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your organization name"
                  className={errors.name ? "border-destructive" : ""}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  This will be displayed across your dashboard and network policies
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                This step is required to complete your account setup. You can change these settings later in your account preferences.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 